import * as THREE from 'three';
import { interpolateBSpline } from '../utils/geometryUtils';
import { ModifierFactory } from '../modifiers/ModifierFactory';
import { createNoise3D } from 'simplex-noise';
export class LampGeometry {
    // Utility: Deep round numbers in an object/array
    static deepRound(obj, decimals = 6) {
        if (Array.isArray(obj)) {
            return obj.map(item => LampGeometry.deepRound(item, decimals));
        }
        else if (obj && typeof obj === 'object') {
            const result = {};
            for (const key of Object.keys(obj)) {
                result[key] = LampGeometry.deepRound(obj[key], decimals);
            }
            return result;
        }
        else if (typeof obj === 'number') {
            return Number(obj.toFixed(decimals));
        }
        else {
            return obj;
        }
    }
    // Utility: Deep compare two objects/arrays
    static deepEqual(a, b) {
        return JSON.stringify(a) === JSON.stringify(b);
    }
    static flattenProfilePoints(points) {
        return points.flatMap(pt => [Number(pt.x.toFixed(6)), Number(pt.y.toFixed(6))]);
    }
    static flattenParams(params) {
        // Only flatten profilePoints, keep other params as is (rounded)
        const { profilePoints, ...rest } = params;
        const roundedRest = {};
        for (const key in rest) {
            if (typeof rest[key] === 'number') {
                roundedRest[key] = Number(rest[key].toFixed(6));
            }
            else {
                roundedRest[key] = rest[key];
            }
        }
        return {
            ...roundedRest,
            profilePoints: LampGeometry.flattenProfilePoints(profilePoints)
        };
    }
    static arraysAlmostEqual(a, b, epsilon = 1e-5) {
        if (!a || !b || a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            if (Math.abs(a[i] - b[i]) > epsilon)
                return false;
        }
        return true;
    }
    constructor(baseShape, modifiers, noiseSeed) {
        this.baseShape = baseShape;
        // Prepare params for comparison (flattened and rounded)
        const params = {
            diameter: baseShape.diameter,
            height: baseShape.height,
            tubularSegments: baseShape.tubularSegments,
            radialSegments: baseShape.radialSegments,
            profilePoints: baseShape.profilePoints
            // noiseSeed is intentionally omitted from logging and comparison
        };
        const flatParams = LampGeometry.flattenParams(params);
        // Create a deterministic random function for the seed
        const random = noiseSeed !== undefined
            ? () => (noiseSeed * 16807) % 2147483647 / 2147483647
            : Math.random;
        this.noise3D = createNoise3D(random);
        this.modifiers = this.initializeModifiers(modifiers);
        // Interpolate profile points and flatten
        const interpolatedPoints = interpolateBSpline(baseShape.profilePoints, baseShape.tubularSegments);
        const flatInterpolated = LampGeometry.flattenProfilePoints(interpolatedPoints);
        // Only log if flattened params or interpolated points have changed
        const paramsChanged = JSON.stringify(flatParams) !== JSON.stringify(LampGeometry.lastLoggedParams);
        const interpolatedChanged = !LampGeometry.arraysAlmostEqual(flatInterpolated, LampGeometry.lastLoggedInterpolated);
        if (paramsChanged || interpolatedChanged) {
            // console.log('[LampGeometry] Random parameters:', flatParams);
            // console.log('[LampGeometry] Interpolated profile points:', flatInterpolated);
            LampGeometry.lastLoggedParams = flatParams;
            LampGeometry.lastLoggedInterpolated = flatInterpolated;
        }
        this.geometry = this.generateBaseGeometry();
        this.applyModifiers();
        this.checkDegenerateTriangles();
    }
    initializeModifiers(modifierData) {
        return modifierData.map(data => ModifierFactory.fromJSON(data, this.noise3D));
    }
    generateBaseGeometry() {
        const { diameter, height, tubularSegments, radialSegments, profilePoints } = this.baseShape;
        // Generate interpolated points
        const interpolatedPoints = interpolateBSpline(profilePoints, tubularSegments);
        // Create points for lathe geometry
        const points = interpolatedPoints.map(point => {
            const x = point.x * (diameter / 2);
            const y = point.y * height;
            return new THREE.Vector2(x, y);
        });
        // Create the base geometry
        const geometry = new THREE.LatheGeometry(points, radialSegments, 0, Math.PI * 2);
        return geometry;
    }
    applyModifiers() {
        const enabledModifiers = this.modifiers.filter(modifier => !modifier.isDisabled());
        this.modifiers.forEach(modifier => {
            if (!modifier.isDisabled()) {
                modifier.apply(this.geometry);
            }
        });
    }
    checkDegenerateTriangles() {
        const posAttr = this.geometry.getAttribute('position');
        const indexAttr = this.geometry.getIndex();
        if (!posAttr)
            return;
        const pos = posAttr.array;
        const indices = indexAttr ? indexAttr.array : undefined;
        const threshold = 1e-6;
        let degenerateCount = 0;
        let triangleCount = 0;
        if (indices) {
            for (let i = 0; i < indices.length; i += 3) {
                const ai = indices[i] * 3, bi = indices[i + 1] * 3, ci = indices[i + 2] * 3;
                const ax = pos[ai], ay = pos[ai + 1], az = pos[ai + 2];
                const bx = pos[bi], by = pos[bi + 1], bz = pos[bi + 2];
                const cx = pos[ci], cy = pos[ci + 1], cz = pos[ci + 2];
                const abx = bx - ax, aby = by - ay, abz = bz - az;
                const acx = cx - ax, acy = cy - ay, acz = cz - az;
                const crossx = aby * acz - abz * acy;
                const crossy = abz * acx - abx * acz;
                const crossz = abx * acy - aby * acx;
                const area = 0.5 * Math.sqrt(crossx * crossx + crossy * crossy + crossz * crossz);
                if (area < threshold) {
                    degenerateCount++;
                    console.warn(`Degenerate triangle found at triangle index ${i / 3}`);
                }
                triangleCount++;
            }
        }
        else {
            for (let i = 0; i < pos.length; i += 9) {
                const ax = pos[i], ay = pos[i + 1], az = pos[i + 2];
                const bx = pos[i + 3], by = pos[i + 4], bz = pos[i + 5];
                const cx = pos[i + 6], cy = pos[i + 7], cz = pos[i + 8];
                const abx = bx - ax, aby = by - ay, abz = bz - az;
                const acx = cx - ax, acy = cy - ay, acz = cz - az;
                const crossx = aby * acz - abz * acy;
                const crossy = abz * acx - abx * acz;
                const crossz = abx * acy - aby * acx;
                const area = 0.5 * Math.sqrt(crossx * crossx + crossy * crossy + crossz * crossz);
                if (area < threshold) {
                    degenerateCount++;
                    console.warn(`Degenerate triangle found at triangle index ${i / 9}`);
                }
                triangleCount++;
            }
        }
        if (degenerateCount > 0) {
            console.warn(`Total degenerate triangles: ${degenerateCount} out of ${triangleCount}`);
        }
    }
    updateModifiers(modifiers) {
        // Dispose of old geometry
        this.geometry.dispose();
        // Create new geometry
        this.geometry = this.generateBaseGeometry();
        // Update modifiers
        this.modifiers = this.initializeModifiers(modifiers);
        // Apply modifiers
        this.applyModifiers();
        this.checkDegenerateTriangles();
    }
    getGeometry() {
        return this.geometry;
    }
    dispose() {
        this.geometry.dispose();
    }
    toJSON() {
        const positions = this.geometry.attributes.position.array;
        const indices = this.geometry.index?.array || [];
        return JSON.stringify({
            positions: Array.from(positions),
            indices: Array.from(indices)
        });
    }
}
LampGeometry.lastLoggedParams = null;
LampGeometry.lastLoggedInterpolated = null;
